/**
 * Ethiopian university–style GPA calculator (4.0 scale).
 * Grade points follow common AAU / university practice.
 */

export type LetterGrade =
  | "A+"
  | "A"
  | "A-"
  | "B+"
  | "B"
  | "B-"
  | "C+"
  | "C"
  | "C-"
  | "D"
  | "F";

export type GpaCourse = {
  id: string;
  name: string;
  credits: number;
  grade: LetterGrade;
};

export const GRADE_POINTS: Record<LetterGrade, number> = {
  "A+": 4.0,
  A: 4.0,
  "A-": 3.75,
  "B+": 3.5,
  B: 3.0,
  "B-": 2.75,
  "C+": 2.5,
  C: 2.0,
  "C-": 1.75,
  D: 1.0,
  F: 0.0,
};

export const LETTER_GRADES = Object.keys(GRADE_POINTS) as LetterGrade[];

const STORAGE_KEY = "soorgaa_gpa_courses";

export function newCourse(partial?: Partial<GpaCourse>): GpaCourse {
  return {
    id: crypto.randomUUID(),
    name: "",
    credits: 3,
    grade: "B",
    ...partial,
  };
}

export function loadGpaCourses(): GpaCourse[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [newCourse({ name: "Course 1" })];
    const data = JSON.parse(raw) as GpaCourse[];
    if (!Array.isArray(data) || data.length === 0) return [newCourse({ name: "Course 1" })];
    return data.map((c) => ({
      id: c.id || crypto.randomUUID(),
      name: typeof c.name === "string" ? c.name : "",
      credits: Number(c.credits) > 0 ? Number(c.credits) : 3,
      grade: (LETTER_GRADES.includes(c.grade as LetterGrade) ? c.grade : "B") as LetterGrade,
    }));
  } catch {
    return [newCourse({ name: "Course 1" })];
  }
}

export function saveGpaCourses(courses: GpaCourse[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(courses));
}

export function calculateGpa(courses: GpaCourse[]): {
  gpa: number | null;
  totalCredits: number;
  qualityPoints: number;
  validCourses: number;
} {
  let qualityPoints = 0;
  let totalCredits = 0;
  let validCourses = 0;

  for (const c of courses) {
    const credits = Number(c.credits);
    if (!Number.isFinite(credits) || credits <= 0) continue;
    const pts = GRADE_POINTS[c.grade];
    if (pts === undefined) continue;
    qualityPoints += pts * credits;
    totalCredits += credits;
    validCourses += 1;
  }

  return {
    gpa: totalCredits > 0 ? qualityPoints / totalCredits : null,
    totalCredits,
    qualityPoints,
    validCourses,
  };
}

export function gpaBand(gpa: number): { label: string; className: string } {
  if (gpa >= 3.75) return { label: "Excellent / First class", className: "text-emerald-700 bg-emerald-50 border-emerald-200" };
  if (gpa >= 3.5) return { label: "Very good / Distinction", className: "text-green-700 bg-green-50 border-green-200" };
  if (gpa >= 3.0) return { label: "Good / Second class", className: "text-blue-700 bg-blue-50 border-blue-200" };
  if (gpa >= 2.0) return { label: "Satisfactory / Pass", className: "text-amber-800 bg-amber-50 border-amber-200" };
  return { label: "Below pass — needs improvement", className: "text-red-700 bg-red-50 border-red-200" };
}
